import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { invoicesApi } from '../services/api';
import { StatusBadge } from '../components/shared/StatusBadge';

export function Dashboard() {
  const all = useQuery({
    queryKey: ['invoices', 'all'],
    queryFn: () => invoicesApi.list({ limit: 200 }),
  });

  if (all.isLoading) return <p className="text-slate-500">Chargement…</p>;
  if (all.isError) return <p className="text-red-600">Erreur : {String(all.error)}</p>;

  const inv = all.data?.invoices ?? [];
  const byStatus = (s: string) => inv.filter((i) => i.status === s).length;
  const pending = inv.filter((i) => i.status === 'pending' || i.status === 'extracted').slice(0, 8);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold tracking-tight">Tableau de bord</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="En attente" value={byStatus('pending')} accent="slate" />
        <KpiCard label="Extraites" value={byStatus('extracted')} accent="amber" />
        <KpiCard label="Validées" value={byStatus('validated')} accent="blue" />
        <KpiCard label="Créées Zoho" value={byStatus('created')} accent="emerald" />
        <KpiCard label="En erreur" value={byStatus('error')} accent="red" />
      </div>

      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-medium">À valider ({pending.length})</h2>
          <Link to="/factures/liste" className="text-sm text-blue-600 hover:underline">
            Voir toutes les factures →
          </Link>
        </div>
        {pending.length === 0 ? (
          <p className="text-slate-500 italic">Aucune facture en attente.</p>
        ) : (
          <ul className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200">
            {pending.map((i) => (
              <li key={i.id}>
                <Link
                  to={`/factures/liste/${i.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{i.filename}</span>
                    <span className="text-xs text-slate-500">
                      {i.source}
                      {i.source_reference ? ` · ${i.source_reference}` : ''} ·{' '}
                      {new Date(i.created_at).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <StatusBadge status={i.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: 'slate' | 'amber' | 'blue' | 'emerald' | 'red';
}) {
  const bg = {
    slate: 'bg-slate-50 text-slate-700',
    amber: 'bg-amber-50 text-amber-800',
    blue: 'bg-blue-50 text-blue-800',
    emerald: 'bg-emerald-50 text-emerald-800',
    red: 'bg-red-50 text-red-800',
  }[accent];
  return (
    <div className={`rounded-lg p-4 ring-1 ring-inset ring-slate-200 ${bg}`}>
      <div className="text-xs uppercase tracking-wide font-medium opacity-80">{label}</div>
      <div className="text-3xl font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
